/**
 * AnythingQueue — Anything Desk View (18 Jul 2026).
 *
 * The white-label pillar's back office: every "Concierge® arrange it"
 * request with the seller provenance the MEMBER never saw — name, link,
 * phone, address — ready for the desk to action. Includes Zoho state
 * (ticket id · routed-to-tenant · retry push) and a 3-step status flow.
 *
 * Reads:  GET  /api/os-admin/anything/requests
 * Writes: POST /api/os-admin/anything/requests/{id}/status
 *         POST /api/os-admin/anything/requests/{id}/push-zoho
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["new", "in_progress", "done"];

function pillTone(s) {
  if (s === "new") return "os-p-new";
  if (s === "in_progress") return "os-p-prog";
  if (s === "done") return "os-p-done";
  return "os-p-new";
}

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch {
    return iso;
  }
}

export default function AnythingQueue() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 25, role: "read_only", has_more: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ status: null, q: "", page: 1, per_page: 25 });
  const [busy, setBusy] = useState(null); // req id being pushed
  const [linkBusy, setLinkBusy] = useState(null); // req id fetching direct links
  const [flash, setFlash] = useState(null); // { id, ok, msg }

  const load = async (f = filters) => {
    setLoading(true);
    setError("");
    try {
      const p = new URLSearchParams();
      if (f.status) p.set("status", f.status);
      if ((f.q || "").trim()) p.set("q", f.q.trim());
      p.set("page", String(f.page));
      p.set("per_page", String(f.per_page));
      const r = await api.get(`/os-admin/anything/requests?${p.toString()}`);
      setRows(r.data.rows || []);
      setMeta({
        total: r.data.total || 0, page: r.data.page || 1,
        per_page: r.data.per_page || 25, role: r.data.role || "read_only",
        has_more: !!r.data.has_more,
      });
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) ||
        "Could not load the Anything queue just now.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filters); /* eslint-disable-next-line */ }, [filters]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/os-admin/anything/requests/${id}/status`, { status });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e) {
      setFlash({ id, ok: false, msg: formatApiError(e?.response?.data?.detail) || "Status update failed" });
    }
  };

  const retryZoho = async (id) => {
    setBusy(id);
    setFlash(null);
    try {
      const r = await api.post(`/os-admin/anything/requests/${id}/push-zoho`);
      if (r.data.ok) {
        setRows((prev) => prev.map((x) => (x.id === id ? { ...x, zoho_ticket_id: r.data.zoho_ticket_id, zoho_pushed: true } : x)));
        setFlash({ id, ok: true, msg: `Zoho ticket ${r.data.zoho_ticket_id}` });
      } else {
        setFlash({ id, ok: false, msg: r.data.detail || "Push failed" });
      }
    } catch (e) {
      setFlash({ id, ok: false, msg: formatApiError(e?.response?.data?.detail) || "Push failed" });
    } finally {
      setBusy(null);
    }
  };

  const fetchDirectLinks = async (id) => {
    setLinkBusy(id);
    setFlash(null);
    try {
      const r = await api.post(`/os-admin/anything/requests/${id}/direct-links`);
      if (r.data.row) {
        setRows((prev) => prev.map((x) => (x.id === id ? r.data.row : x)));
      }
      setFlash(r.data.ok
        ? { id, ok: true, msg: `${r.data.resolved} direct link${r.data.resolved > 1 ? "s" : ""} fetched` }
        : { id, ok: false, msg: r.data.detail || "No direct link found" });
    } catch (e) {
      setFlash({ id, ok: false, msg: formatApiError(e?.response?.data?.detail) || "Lookup failed" });
    } finally {
      setLinkBusy(null);
    }
  };

  const isReadOnly = meta.role === "read_only";

  return (
    <div className="os-fyi-queue" data-testid="os-anything-queue">
      <div className="os-pagehead">
        <h1>Anything Desk</h1>
        <span className="count">{loading ? "Loading…" : `${meta.total} requests`}</span>
      </div>
      <div className="os-pagesub">
        White-label provenance · <code>anything_requests</code> · the member never
        saw the seller — this desk does · role: <strong data-testid="os-anything-role">{meta.role}</strong>
      </div>

      <div className="os-fyi-filterblock" data-testid="os-anything-filters">
        <div className="os-toolbar">
          <span className="os-fyi-label">Status</span>
          <button type="button"
                  className={`os-fchip ${!filters.status ? "on" : ""}`}
                  onClick={() => setFilters((p) => ({ ...p, status: null, page: 1 }))}
                  data-testid="os-anything-filter-all">
            all
          </button>
          {STATUSES.map((s) => (
            <button key={s} type="button"
                    className={`os-fchip ${filters.status === s ? "on" : ""}`}
                    onClick={() => setFilters((p) => ({ ...p, status: s, page: 1 }))}
                    data-testid={`os-anything-filter-${s}`}>
              {s.replace("_", " ")}
            </button>
          ))}
          <span className="os-spacer" />
          <input type="search"
                 placeholder="Search ask · pick · member"
                 value={filters.q}
                 onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value, page: 1 }))}
                 maxLength={80}
                 className="os-fyi-search"
                 data-testid="os-anything-search" />
        </div>
      </div>

      {error ? (
        <div className="os-fyi-msg os-fyi-msg-err" data-testid="os-anything-error">{error}</div>
      ) : rows.length === 0 && !loading ? (
        <div className="os-fyi-msg" data-testid="os-anything-empty">
          No Anything requests match these filters.
        </div>
      ) : (
        <table className="os-tbl" data-testid="os-anything-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Member</th>
              <th>The ask</th>
              <th>The pick</th>
              {!isReadOnly && <th>Seller (desk only)</th>}
              <th>Zoho</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`os-anything-row-${r.id}`}>
                <td title={r.created_at}>{relativeTime(r.created_at)}</td>
                <td>
                  <div className="os-fyi-title">{r.member_name || "—"}</div>
                  {!isReadOnly && r.member_phone && (
                    <div className="os-fyi-sub">{r.member_phone}</div>
                  )}
                </td>
                <td>
                  <div className="os-fyi-title" style={{ maxWidth: 220 }}>
                    {r.member_ask || "—"}
                  </div>
                  {r.member_note && <div className="os-fyi-sub">note: {r.member_note}</div>}
                </td>
                <td>
                  <div className="os-fyi-title" style={{ maxWidth: 220 }}>{r.chosen || "—"}</div>
                  <div className="os-fyi-sub">
                    {[r.kind, r.price].filter(Boolean).join(" · ")}
                  </div>
                </td>
                {!isReadOnly && (
                  <td data-testid={`os-anything-seller-${r.id}`}>
                    {Array.isArray(r.items) && r.items.length > 1 ? (
                      r.items.map((it, i) => (
                        <div key={i} className="os-fyi-sub" style={{ maxWidth: 240 }}>
                          {i + 1}. {(it.title || "—").slice(0, 40)}
                          {it.seller ? ` · ${it.seller}` : ""}
                          {it.seller_link && (
                            <>
                              {" "}
                              <a href={it.seller_link} target="_blank" rel="noreferrer"
                                 className="os-ref">↗</a>
                            </>
                          )}
                          {it.direct_link && (
                            <>
                              {" "}
                              <a href={it.direct_link} target="_blank" rel="noreferrer"
                                 className="os-ref" style={{ fontWeight: 700 }}
                                 title={`Direct merchant page${it.direct_seller ? ` · ${it.direct_seller}` : ""}`}
                                 data-testid={`os-anything-direct-item-${r.id}-${i}`}>
                                direct ⚡
                              </a>
                            </>
                          )}
                        </div>
                      ))
                    ) : r.source || r.source_link || r.source_phone || r.source_address ? (
                      <>
                        {r.source && <div className="os-fyi-title" style={{ maxWidth: 200 }}>{r.source}</div>}
                        {r.source_link && (
                          <div className="os-fyi-sub">
                            <a href={r.source_link} target="_blank" rel="noreferrer"
                               className="os-ref"
                               data-testid={`os-anything-seller-link-${r.id}`}>
                              open listing ↗
                            </a>
                          </div>
                        )}
                        {r.direct_link && (
                          <div className="os-fyi-sub">
                            <a href={r.direct_link} target="_blank" rel="noreferrer"
                               className="os-ref" style={{ fontWeight: 700 }}
                               data-testid={`os-anything-direct-link-${r.id}`}>
                              direct merchant page ⚡
                            </a>
                            {r.direct_price ? ` · ${r.direct_price}` : ""}
                          </div>
                        )}
                        {r.source_phone && <div className="os-fyi-sub">{r.source_phone}</div>}
                        {r.source_address && (
                          <div className="os-fyi-sub" style={{ maxWidth: 220 }}>{r.source_address}</div>
                        )}
                      </>
                    ) : <span className="os-fyi-dim">—</span>}
                    {r.immersive_available && (
                      <button type="button"
                              className="os-btn-ghost"
                              style={{ marginTop: 4 }}
                              disabled={linkBusy === r.id}
                              onClick={() => fetchDirectLinks(r.id)}
                              title="One SerpApi call · swaps the Google page for the merchant's own product URL"
                              data-testid={`os-anything-direct-fetch-${r.id}`}>
                        {linkBusy === r.id ? "Fetching…" : "Direct links ⚡"}
                      </button>
                    )}
                  </td>
                )}
                <td>
                  {r.routed_to === "mindescapes.club" ? (
                    <span className="os-pill os-p-prog" title="Ticket lives in the tenant's Zoho org">
                      tenant zoho
                    </span>
                  ) : r.zoho_ticket_id ? (
                    <span className="os-ref" data-testid={`os-anything-zoho-${r.id}`}>
                      {r.zoho_ticket_id}
                    </span>
                  ) : r.zoho_skip_reason === "test_traffic" ? (
                    <span className="os-fyi-dim" title="Test payload · push skipped by guard">test · skipped</span>
                  ) : (
                    <button type="button"
                            className="os-btn-ghost"
                            disabled={busy === r.id || isReadOnly}
                            onClick={() => retryZoho(r.id)}
                            data-testid={`os-anything-retry-${r.id}`}>
                      {busy === r.id ? "Pushing…" : "Retry Zoho ↻"}
                    </button>
                  )}
                  {flash && flash.id === r.id && (
                    <div className={flash.ok ? "os-fyi-sub" : "os-fyi-sub os-fyi-msg-err"}
                         data-testid={`os-anything-flash-${r.id}`}
                         style={{ marginTop: 4 }}>
                      {flash.msg}
                    </div>
                  )}
                </td>
                <td>
                  {isReadOnly ? (
                    <span className={`os-pill ${pillTone(r.status)}`}>
                      {(r.status || "new").replace("_", " ")}
                    </span>
                  ) : (
                    <select value={r.status || "new"}
                            onChange={(e) => setStatus(r.id, e.target.value)}
                            className="os-fyi-search"
                            style={{ padding: "4px 8px" }}
                            data-testid={`os-anything-status-${r.id}`}>
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s.replace("_", " ")}</option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="os-fyi-pagi" data-testid="os-anything-pagination">
        <button type="button" className="os-btn-ghost"
                disabled={meta.page <= 1 || loading}
                onClick={() => setFilters((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                data-testid="os-anything-page-prev">‹ Prev</button>
        <span className="os-fyi-pagemark">Page {meta.page} · {meta.per_page}/page</span>
        <button type="button" className="os-btn-ghost"
                disabled={!meta.has_more || loading}
                onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
                data-testid="os-anything-page-next">Next ›</button>
      </div>
    </div>
  );
}
